import { includes } from 'lodash';
import { colors } from '../../global/variables';
import { metersToKilometers, metersToMiles, secondsToHours, secondsToMinutes } from '../converter';

export const getGeoInfoFromObjects = (objList) =>
  objList && {
    type: 'FeatureCollection',
    features: Object.keys(objList)
      .filter((key) => objList[key]?.geo)
      .map((key) => objList[key].geo.map((feature) => feature))
      .flat(),
  };

export const getOriginalLines = (objList) =>
  objList && {
    type: 'FeatureCollection',
    features: Object.values(objList).flatMap((value) => value.originalLocation),
  };

export const formatSegmentDuration = (seconds) => {
  if (seconds == null) return null;
  const totalHours = secondsToHours(seconds);
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export const formatSegmentDistance = (meters, isImperial) => {
  if (meters == null) return null;
  if (isImperial) return `${metersToMiles(meters).toFixed(1)} mi`;
  return `${metersToKilometers(meters).toFixed(1)} km`;
};

export const getSegmentBatteryUsage = (tour, routeId, fromStop) => {
  const stopFeatures = tour?.geo?.features?.filter(
    (f) =>
      f.geometry.type === 'Point' &&
      f.properties.routeId === routeId &&
      f.properties.jobOrder != null,
  );
  if (!stopFeatures || stopFeatures.length === 0) return null;

  const fromFeature = stopFeatures.find((f) => f.properties.jobOrder === fromStop);
  const toFeature = stopFeatures.find((f) => f.properties.jobOrder === fromStop + 1);
  const batteryCapacity = fromFeature?.properties.batteryCapacity;
  const fromConsumption = fromFeature?.properties.consumption;
  const toConsumption = toFeature?.properties.consumption;

  if (batteryCapacity == null || fromConsumption == null || toConsumption == null) return null;
  if (batteryCapacity <= 0) return null;

  const usedKwh = toConsumption - fromConsumption;
  return (usedKwh / batteryCapacity) * 100;
};

export const formatSegmentBatteryUsage = (percentage) => {
  if (percentage == null) return null;
  return `${percentage.toFixed(2)}%`;
};

const buildLocationKey = (lat, lng) =>
  lat != null && lng != null ? `${parseFloat(lat).toFixed(5)},${parseFloat(lng).toFixed(5)}` : null;

export const getSegmentParkingWalkDistance = (
  tour,
  routeId,
  fromStop,
  walkingDistancesMap,
  isImperial,
) => {
  if (!walkingDistancesMap) return null;

  const stopFeatures = tour?.geo?.features?.filter(
    (f) =>
      f.geometry.type === 'Point' &&
      f.properties.routeId === routeId &&
      f.properties.jobOrder != null,
  );
  if (!stopFeatures || stopFeatures.length === 0) return null;

  const fromFeature = stopFeatures.find((f) => f.properties.jobOrder === fromStop);
  const toFeature = stopFeatures.find((f) => f.properties.jobOrder === fromStop + 1);
  const parkingFeature = [fromFeature, toFeature].find((f) => f?.properties.isParking);
  if (!parkingFeature) return null;

  const [lng, lat] = parkingFeature.geometry.coordinates;
  const key = buildLocationKey(lat, lng);
  const meters = key ? walkingDistancesMap[key] : null;
  return meters != null ? formatSegmentDistance(meters, isImperial) : null;
};

export const onEachFeature = (
  feature,
  layer,
  mapData,
  display,
  onTourLineClick,
  onToggleHiddenSegment,
  isImperial,
  routingTour,
) => {
  if (feature.geometry.type === 'LineString') {
    const tour = mapData?.geo?.features.find(
      (f) =>
        f.geometry.type === 'LineString' &&
        (display.selectedTour !== undefined
          ? display.selectedTour === f.properties.routeId
          : includes(display.routeIds, f.properties.routeId)),
    );
    if (tour) {
      const { isStopsRequest, fromStop, segmentDuration, segmentDistance, segmentBaseDuration } =
        feature.properties;

      const distance = formatSegmentDistance(segmentDistance, isImperial);
      const duration = formatSegmentDuration(segmentDuration);
      const baseDuration = formatSegmentDuration(segmentBaseDuration);

      const durationMins =
        segmentDuration != null ? Math.floor(secondsToMinutes(segmentDuration)) : null;
      const baseMins =
        segmentBaseDuration != null ? Math.floor(secondsToMinutes(segmentBaseDuration)) : null;
      const delayMins = durationMins != null && baseMins != null ? durationMins - baseMins : null;
      const delayText =
        delayMins != null && delayMins > 0
          ? `<tr>
               <td>Delay</td>
               <td>+${delayMins}m</td>
             </tr>`
          : '';

      if (isStopsRequest && distance != null && duration != null) {
        const { routeId } = feature.properties;
        const batteryUsage = getSegmentBatteryUsage(mapData, routeId, fromStop);
        const batteryText =
          batteryUsage != null
            ? `<tr><td>Battery used</td><td>${formatSegmentBatteryUsage(batteryUsage)}</td></tr>`
            : '';
        const parkingDistance = getSegmentParkingWalkDistance(
          mapData,
          routeId,
          fromStop,
          routingTour?.[routeId]?.walkingDistancesMap,
          isImperial,
        );
        const parkingDistanceText =
          parkingDistance != null
            ? `<tr><td>Parking distance</td><td>${parkingDistance}</td></tr>`
            : '';

        const segmentRows = `
           <tr><td>Stop</td><td>${fromStop} → ${fromStop + 1}</td></tr>
           <tr><td>Distance</td><td>${distance}</td></tr>
           <tr><td>Drive time</td><td>${duration}</td></tr>
           ${delayText}
           ${
             baseDuration && baseDuration !== duration
               ? `<tr><td style="color:${colors.primaryTextColor}">Free flow</td><td style="color:${colors.primaryTextColor}">${baseDuration}</td></tr>`
               : ''
           }
           ${batteryText}
           ${parkingDistanceText}
          `;
        layer.bindTooltip(`<table>${segmentRows}</table>`, { sticky: true });
      } else if (feature.properties.mode === 'pedestrian' && distance != null && duration != null) {
        const pedestrianRows = `
           <tr><td>Distance</td><td>${distance}</td></tr>
           <tr><td>Walk time</td><td>${duration}</td></tr>
          `;
        layer.bindTooltip(`<table>${pedestrianRows}</table>`, { sticky: true });
      }

      const isHitArea = layer.options?.opacity === 0;
      const { routeId } = feature.properties;
      // Include mode so a pedestrian leg (e.g. parking to stop) never shares a segment key with
      // a driving segment that happens to have the same routeId/fromStop, which previously
      // caused hovering one to highlight both.
      const segmentKey = `${routeId}-${fromStop}-${feature.properties.mode || 'default'}`;

      layer.on('add', () => {
        const el = layer.getElement();
        if (el) el.dataset.segment = segmentKey;
      });

      layer.on({
        click: () => onTourLineClick(feature),
        contextmenu: () => onToggleHiddenSegment && onToggleHiddenSegment(feature),
      });

      if (isHitArea) {
        layer.on({
          mouseover() {
            document.querySelectorAll(`[data-segment="${segmentKey}"]`).forEach((el) => {
              if (el.dataset.origStrokeWidth !== undefined) return;
              const w = parseFloat(el.getAttribute('stroke-width') || '2');
              const stroke = el.getAttribute('stroke') || '#888';
              el.dataset.origStrokeWidth = String(w);
              el.setAttribute('stroke-width', String(w + 4));
              el.style.filter = `drop-shadow(0 0 8px ${stroke})`;
            });
          },
          mouseout() {
            document.querySelectorAll(`[data-segment="${segmentKey}"]`).forEach((el) => {
              if (el.dataset.origStrokeWidth !== undefined) {
                el.setAttribute('stroke-width', el.dataset.origStrokeWidth);
                delete el.dataset.origStrokeWidth;
              }
              el.style.filter = '';
            });
          },
        });
      }
    }
  }
};
